import React, { Fragment, useEffect, useMemo, useState, useContext } from "react";
import { auth } from "../firebaseConfig";
import { addClothing, updateClothing, fetchClothes } from "./clothingService";
import SuccessAnimation from "../components/SuccessAnimation";
import { Dialog, Transition } from "@headlessui/react";
import { useDropzone } from "react-dropzone";
import {
  XMarkIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import { analyzeClothingImage } from "../services/clothingAnalysisService";
import { useImport } from "../context/ImportContext";
import { AuthContext } from "../context/AuthContext";
import { PLAN_LIMITS } from "../services/userService";
import { ALWAYS_PREMIUM } from "../config/featureFlags";

const TYPE_OPTIONS = [
  { value: "sweat", label: "Sweat à capuche" },
  { value: "t-shirt", label: "T-shirt" },
  { value: "pantalon", label: "Pantalon" },
  { value: "robe", label: "Robe" },
  { value: "veste", label: "Veste" },
  { value: "chaussures", label: "Chaussures" },
  { value: "accessoires", label: "Accessoires" },
  { value: "manteau", label: "Manteau" },
];

const SEASONS = ["printemps", "été", "automne", "hiver"];

const COLORS = [
  { value: "noir", swatch: "#111827", label: "Noir" },
  { value: "blanc", swatch: "#F9FAFB", label: "Blanc", ring: "#E5E7EB" },
  { value: "gris", swatch: "#9CA3AF", label: "Gris" },
  { value: "bleu", swatch: "#3B82F6", label: "Bleu" },
  { value: "rouge", swatch: "#EF4444", label: "Rouge" },
  { value: "vert", swatch: "#10B981", label: "Vert" },
  { value: "jaune", swatch: "#F59E0B", label: "Jaune" },
  { value: "beige", swatch: "#F5E6CA", label: "Beige", ring: "#E5D3A0" },
  { value: "rose", swatch: "#F472B6", label: "Rose" },
  { value: "marron", swatch: "#8B5E3C", label: "Marron" },
  { value: "violet", swatch: "#8B5CF6", label: "Violet" },
];

const OCCASIONS = ["casual", "travail", "sport", "soirée"];

const MAX_IMAGE_BYTES = 900_000; // Juste sous la limite Firestore (~1 Mo)
const MAX_DIMENSION = 1280; // pixels
const DEFAULT_QUALITY = 0.85;
const MIN_QUALITY = 0.4;

async function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

async function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = src;
  });
}

async function compressImage(file) {
  const originalDataUrl = await readFileAsDataURL(file);
  const image = await loadImage(originalDataUrl);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  let { width, height } = image;
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  canvas.width = width;
  canvas.height = height;
  ctx.drawImage(image, 0, 0, width, height);

  let quality = DEFAULT_QUALITY;
  let output = canvas.toDataURL("image/jpeg", quality);

  while (output.length > MAX_IMAGE_BYTES && quality >= MIN_QUALITY) {
    quality -= 0.1;
    output = canvas.toDataURL("image/jpeg", quality);
  }

  if (output.length > MAX_IMAGE_BYTES) {
    throw new Error("Image trop volumineuse même après compression.");
  }

  return output;
}

export default function AddClothingModal({ isOpen, onClose, initialData, isEditing }) {
  const { userProfile } = useContext(AuthContext);
  const { startBulkImport } = useImport();
  const [formData, setFormData] = useState({
    type: "",
    colors: [],
    season: "",
    occasions: [],
    image: null, // base64
    details: "",
  });
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState("");
  const [hasAiSuggestion, setHasAiSuggestion] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionError, setCompressionError] = useState("");
  // Ajout multiple
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkTotal, setBulkTotal] = useState(0);
  const [bulkProcessed, setBulkProcessed] = useState(0);
  const [bulkFailed, setBulkFailed] = useState(0);
  const [bulkRunning, setBulkRunning] = useState(false);

  // Utils
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const guessTypeFromFilename = (name = "") => {
    const n = (name || "").toLowerCase();
    const tests = [
      { re: /(chaussure|sneaker|sneakers|basket|shoe|trainer|adidas|nike|jordan)/, type: "chaussures" },
      { re: /(pantalon|jean|jeans|pant|trouser|cargo|chino|jogger)/, type: "pantalon" },
      { re: /(veste|jacket|blouson|anorak|parka|gilet|cardigan)/, type: "veste" },
      { re: /(manteau|coat|overcoat)/, type: "manteau" },
      { re: /(robe|dress)/, type: "robe" },
      { re: /(sweat|hoodie|crewneck|pull|sweater|hooded)/, type: "sweat" },
      { re: /(t[-\s]?shirt|tee|tshirt)/, type: "t-shirt" },
      { re: /(ceinture|sac|bag|chapeau|hat|bonnet|beanie|écharpe|scarf|accessoire)/, type: "accessoires" },
    ];
    for (const t of tests) if (t.re.test(n)) return t.type;
    return "t-shirt"; // fallback raisonnable
  };

  // Dominant color extraction and mapping to our labels
  const getDominantColorLabel = async (dataUrl) => {
    try {
      const img = await loadImage(dataUrl);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      // Downscale aggressively for speed
      const w = 32, h = 32;
      canvas.width = w; canvas.height = h;
      ctx.drawImage(img, 0, 0, w, h);
      const { data } = ctx.getImageData(0, 0, w, h);
      let r = 0, g = 0, b = 0, count = 0;
      for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a < 10) continue; // ignore transparent
        r += data[i]; g += data[i + 1]; b += data[i + 2];
        count++;
      }
      if (!count) return "";
      r = Math.round(r / count); g = Math.round(g / count); b = Math.round(b / count);
      const [hue, sat, lig] = rgbToHsl(r, g, b);
      return mapHslToColor(hue, sat, lig);
    } catch {
      return "";
    }
  };

  const rgbToHsl = (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) { h = 0; s = 0; }
    else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    return [Math.round(h * 360), s, l];
  };

  const mapHslToColor = (h, s, l) => {
    if (l <= 0.12) return "noir";
    if (l >= 0.92) return "blanc";
    if (s <= 0.12) {
      // désaturé
      if (l > 0.7) return "beige"; // tons clairs
      return "gris";
    }
    // Couleurs vives par plages de teinte
    if (h < 15 || h >= 345) return "rouge";
    if (h < 35) return l < 0.4 ? "marron" : "orange";
    if (h < 65) return "jaune";
    if (h < 170) return "vert";
    if (h < 250) return "bleu";
    if (h < 290) return "violet";
    return "rose";
  };

  const analyzeWithRetry = async (dataUrl, attempts = 8) => {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try {
        return await analyzeClothingImage(dataUrl);
      } catch (e) {
        lastErr = e;
        // 429 rate limit: backoff
        const msg = String(e?.message || '');
        const is429 = e?.status === 429 || msg.includes('rate_limit');
        if (is429) {
          // Prefer server-provided Retry-After
          if (typeof e.retryAfterMs === 'number' && e.retryAfterMs > 0) {
            await sleep(e.retryAfterMs + 200);
            continue;
          }
          // Try to parse suggested delay from message
          let wait = 1000 * Math.pow(2, i) + Math.floor(Math.random() * 400);
          const m = msg.match(/try again in\s+(\d+)ms/i);
          if (m && m[1]) {
            wait = parseInt(m[1], 10) + 250; // small cushion
          }
          await sleep(wait);
          continue;
        }
        break;
      }
    }
    throw lastErr;
  };

  const autoFillFromImage = async (dataUrl) => {
    if (!dataUrl) return;
    try {
      setAnalysisError("");
      setIsAnalyzing(true);
      setHasAiSuggestion(false);
      const suggestion = await analyzeClothingImage(dataUrl);
      setFormData((prev) => ({
        ...prev,
        type: suggestion.type || prev.type,
        colors: suggestion.colors?.length ? suggestion.colors : prev.colors,
        season: suggestion.season || prev.season,
        occasions: suggestion.occasions?.length ? suggestion.occasions : prev.occasions,
        title: suggestion.title || prev.title || "",
        details: suggestion.details || prev.details,
      }));
      setHasAiSuggestion(true);
    } catch (err) {
      console.error("Analyse IA vêtement :", err);
      setAnalysisError(err.message || "Impossible d'analyser la photo.");
      setHasAiSuggestion(false);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Pré-remplissage
  useEffect(() => {
    if (!isOpen) return;
    if (isEditing && initialData) {
      setFormData({
        type: initialData.type || "",
        colors: Array.isArray(initialData.colors) ? initialData.colors : (initialData.color ? [initialData.color] : []),
        season: initialData.season || "",
        occasions: initialData.occasions || [],
        image: initialData.image || null,
        title: initialData.title || "",
        details: initialData.details || "",
      });
    } else {
      setFormData({
        type: "",
        colors: [],
        season: "",
        occasions: [],
        image: null,
        title: "",
        details: "",
      });
    }
    setError("");
    setAnalysisError("");
    setIsAnalyzing(false);
    setHasAiSuggestion(false);
    setIsCompressing(false);
    setCompressionError("");
    // reset bulk state
    setBulkMode(false);
    setBulkTotal(0);
    setBulkProcessed(0);
    setBulkFailed(0);
    setBulkRunning(false);
  }, [isOpen, isEditing, initialData]);

  const computeMaxClothes = () => {
    const plan = userProfile?.plan || "free";
    let maxClothes;
    if (ALWAYS_PREMIUM) {
      maxClothes = null;
    } else {
      const userLimit = userProfile?.limits?.maxClothes;
      const planLimit = PLAN_LIMITS[plan]?.maxClothes;
      if (userLimit !== undefined) {
        maxClothes = userLimit; // null => illimité
      } else if (planLimit !== undefined) {
        maxClothes = planLimit; // null => illimité
      } else {
        maxClothes = PLAN_LIMITS.free.maxClothes; // fallback ultime
      }
    }
    return maxClothes;
  };

  const startBulkAdd = async (files) => {
    if (!auth.currentUser) {
      setError("Connectez-vous pour ajouter des vêtements.");
      return;
    }
    setBulkMode(true);
    setBulkRunning(true);
    setCompressionError("");
    setError("");

    try {
      const maxClothes = computeMaxClothes();
      let allowedFiles = files;
      if (maxClothes !== null) {
        try {
          const existing = await fetchClothes(auth.currentUser.uid);
          const remaining = Math.max(0, maxClothes - existing.length);
          if (remaining <= 0) {
            setBulkRunning(false);
            setBulkMode(false);
            setError(`Limite atteinte ! Vous avez déjà ${maxClothes} vêtements. Passez au Premium pour un ajout illimité.`);
            return;
          }
          if (files.length > remaining) {
            allowedFiles = files.slice(0, remaining);
          }
        } catch (e) {
          console.error("Erreur lors du calcul de la limite (bulk)", e);
        }
      }

      // Limite stricte par lot pour l'IA
      const MAX_FILES_PER_BATCH = 20;
      if (allowedFiles.length > MAX_FILES_PER_BATCH) {
        setError(`Limite: ${MAX_FILES_PER_BATCH} images par import. Les ${MAX_FILES_PER_BATCH} premières seront analysées, réimportez le reste.`);
        allowedFiles = allowedFiles.slice(0, MAX_FILES_PER_BATCH);
      }

      setBulkTotal(allowedFiles.length);
      setBulkProcessed(0);
      setBulkFailed(0);

      for (const file of allowedFiles) {
        try {
          // Compression ou fallback
          let dataUrl;
          try {
            dataUrl = await compressImage(file);
          } catch (cmpErr) {
            dataUrl = await readFileAsDataURL(file);
            if (!dataUrl || dataUrl.length > MAX_IMAGE_BYTES) {
              throw new Error("Image trop volumineuse");
            }
          }

          // Analyse IA stricte pour chaque image (avec retry/backoff)
          const suggestion = await analyzeWithRetry(dataUrl, 6);
          if (!suggestion || !suggestion.type) {
            throw new Error("Analyse IA incomplète (type manquant)");
          }

          const payload = {
            type: suggestion.type,
            colors: Array.isArray(suggestion.colors) ? suggestion.colors : [],
            season: suggestion.season || "",
            occasions: suggestion.occasions || [],
            title: (suggestion.title || "").trim(),
            details: suggestion.details || "",
            image: dataUrl,
            userId: auth.currentUser.uid,
            createdAt: new Date(),
          };

          await addClothing(payload);
        } catch (fileErr) {
          console.error("Échec ajout (bulk) pour un fichier:", fileErr);
          setBulkFailed((n) => n + 1);
        } finally {
          setBulkProcessed((n) => n + 1);
        }
        // pause entre images pour ménager le rate-limit (min interval)
        await sleep(2000);
      }

      setBulkRunning(false);
      // petite animation puis fermeture
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 900);
    } catch (err) {
      console.error("Erreur bulk:", err);
      setError(err.message || "Erreur pendant l'ajout multiple.");
      setBulkRunning(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    if (acceptedFiles.length > 1) {
      // Lancer l'import IA en arrière-plan et fermer la modale
      startBulkImport(acceptedFiles, userProfile);
      onClose();
      return;
    }
    const file = acceptedFiles[0];
    try {
      setCompressionError("");
      setIsCompressing(true);
      const processedDataUrl = await compressImage(file);
      setFormData((prev) => ({ ...prev, image: processedDataUrl }));
      setIsCompressing(false);
      autoFillFromImage(processedDataUrl);
    } catch (err) {
      console.error("Compression image:", err);
      setCompressionError(err.message || "Impossible de compresser cette image.");
      setIsCompressing(false);
      try {
        const fallback = await readFileAsDataURL(file);
        if (fallback.length > MAX_IMAGE_BYTES) {
          setError("L'image sélectionnée est trop volumineuse pour être enregistrée.");
          return;
        }
        setFormData((prev) => ({ ...prev, image: fallback }));
        autoFillFromImage(fallback);
      } catch (fallbackErr) {
        console.error("Lecture fichier:", fallbackErr);
        setError("Impossible de lire cette image.");
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: true,
    maxFiles: 20,
  });

  const toggleOccasion = (occ) => {
    setFormData((prev) => {
      const exists = prev.occasions.includes(occ);
      return {
        ...prev,
        occasions: exists ? prev.occasions.filter((o) => o !== occ) : [...prev.occasions, occ],
      };
    });
  };

  const toggleColor = (value) => {
    setFormData((prev) => {
      const exists = prev.colors.includes(value);
      return {
        ...prev,
        colors: exists ? prev.colors.filter((c) => c !== value) : [...prev.colors, value],
      };
    });
  };
  const setSeason = (value) => setFormData((prev) => ({ ...prev, season: prev.season === value ? "" : value }));

  const detailsCount = useMemo(() => formData.details?.length || 0, [formData.details]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (!auth.currentUser) {
      setError("Connectez-vous pour ajouter un vêtement.");
      return;
    }

    if (formData.image && formData.image.length > MAX_IMAGE_BYTES) {
      setError("L'image est trop volumineuse. Réessayez avec une image plus légère.");
      return;
    }

    if (!formData.type) return setError("Sélectionne un type de vêtement.");
    
    // Vérifier la limite de vêtements pour le plan gratuit (sauf en mode édition)
    if (!isEditing) {
      const plan = userProfile?.plan || "free";
      // Important: ne pas tomber sur la limite du plan gratuit quand maxClothes est null (illimité)
      let maxClothes;
      if (ALWAYS_PREMIUM) {
        maxClothes = null;
      } else {
        const userLimit = userProfile?.limits?.maxClothes;
        const planLimit = PLAN_LIMITS[plan]?.maxClothes;
        if (userLimit !== undefined) {
          maxClothes = userLimit; // null => illimité
        } else if (planLimit !== undefined) {
          maxClothes = planLimit; // null => illimité
        } else {
          maxClothes = PLAN_LIMITS.free.maxClothes; // fallback ultime
        }
      }

      if (maxClothes !== null) {
        try {
          const existingClothes = await fetchClothes(auth.currentUser.uid);
          if (existingClothes.length >= maxClothes) {
            setError(`Limite atteinte ! Vous avez déjà ${maxClothes} vêtements. Passez au Premium pour un ajout illimité de vêtements.`);
            return;
          }
        } catch (err) {
          console.error("Erreur lors de la vérification de la limite:", err);
        }
      }
    }
    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        userId: auth.currentUser.uid,
        occasions: formData.occasions || [],
        createdAt: isEditing ? initialData?.createdAt || new Date() : new Date(),
      };

      if (isEditing) {
        await updateClothing(initialData.id, payload);
      } else {
        await addClothing(payload);
      }

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 1100);
    } catch (err) {
      console.error("Erreur complète :", err);
      setError(err.message || "Erreur technique");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[rgba(0,0,0,0.35)] backdrop-blur-sm" />
        </Transition.Child>

        {/* Panel */}
        <div className="fixed inset-0 grid place-items-center p-4">
          <Transition.Child
            as={Fragment}
            enter="transition-all ease-out duration-200"
            enterFrom="opacity-0 translate-y-2 scale-[0.98]"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="transition-all ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-1 scale-[0.98]"
          >
            <Dialog.Panel className="w-full max-w-2xl rounded-3xl border border-white/60 bg-white/80 backdrop-blur-xl shadow-[0_20px_70px_rgba(16,24,40,0.15)]">
              {/* Header */}
              <div className="relative px-6 md:px-8 pt-6 md:pt-8 pb-4">
                <Dialog.Title className="text-2xl md:text-3xl font-semibold text-neutral-900">
                  {isEditing ? "Modifier le vêtement" : "Ajoutez du style"}
                </Dialog.Title>
                <button
                  onClick={onClose}
                  className="absolute right-4 top-4 w-10 h-10 rounded-full grid place-items-center hover:bg-white/70 border border-white/50"
                  aria-label="Fermer"
                >
                  <XMarkIcon className="w-5 h-5 text-neutral-700" />
                </button>
              </div>

              <SuccessAnimation show={showSuccess} isEditing={isEditing} />

              {/* Body - Scrollable container */}
              {!showSuccess && (
              <form onSubmit={handleSubmit} className="flex flex-col max-h-[70vh]">
                <div className="px-6 md:px-8 overflow-y-auto flex-1">
                {error && (
                  <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    {error}
                  </div>
                )}

                {/* Image uploader */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Image{bulkMode ? 's' : ''}</label>
                  <div
                    {...getRootProps()}
                    className={`rounded-2xl border-2 border-dashed ${
                      isDragActive ? "border-black" : "border-neutral-300"
                    } bg-white/70 hover:bg-white transition p-4 md:p-5 cursor-pointer`}
                  >
                    <input {...getInputProps()} />
                    {bulkMode ? (
                      <div className="space-y-2">
                        <div className="text-sm text-neutral-700">
                          {bulkRunning ? `Ajout de ${bulkTotal} vêtement(s)…` : `Sélection multiple : ${bulkTotal} fichier(s)`}
                        </div>
                        <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-black transition-all"
                            style={{ width: bulkTotal ? `${Math.round((bulkProcessed / bulkTotal) * 100)}%` : '0%' }}
                          />
                        </div>
                        {(bulkProcessed > 0 || bulkFailed > 0) && (
                          <div className="text-xs text-neutral-500">
                            {bulkProcessed}/{bulkTotal} traités{bulkFailed ? ` • ${bulkFailed} échec(s)` : ''}
                          </div>
                        )}
                      </div>
                    ) : formData.image ? (
                      <div className="flex items-center gap-4">
                        <img
                          src={formData.image}
                          alt="Aperçu"
                          className="w-28 h-28 md:w-36 md:h-36 rounded-xl object-cover border border-neutral-200"
                        />
                        <div className="text-sm text-neutral-600">
                          <p className="font-medium text-neutral-800 mb-1">Image sélectionnée</p>
                          <p className="text-neutral-500">Glissez-déposez une nouvelle image pour remplacer.</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4">
                        <div className="w-28 h-28 md:w-36 md:h-36 rounded-xl grid place-items-center bg-neutral-100 border border-neutral-200">
                          <PhotoIcon className="w-8 h-8 text-neutral-400" />
                        </div>
                        <div className="text-sm text-neutral-600">
                          <p className="font-medium text-neutral-800">Déposez une ou plusieurs images ici</p>
                          <p className="text-neutral-500">ou cliquez pour sélectionner des fichiers (JPEG/PNG)</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {!bulkMode && isCompressing && (
                    <p className="mt-3 text-sm text-neutral-600 flex items-center gap-2">
                      <span className="inline-block w-4 h-4 border-2 border-neutral-300 border-t-black rounded-full animate-spin" />
                      Compression de l'image...
                    </p>
                  )}
                  {!bulkMode && !isCompressing && isAnalyzing && (
                    <p className="mt-3 text-sm text-neutral-600 flex items-center gap-2">
                      <span className="inline-block w-4 h-4 border-2 border-neutral-300 border-t-black rounded-full animate-spin" />
                      Analyse du vêtement en cours...
                    </p>
                  )}
                  {!bulkMode && !isCompressing && !isAnalyzing && hasAiSuggestion && !analysisError && (
                    <p className="mt-3 text-sm text-emerald-600">
                      Suggestion IA appliquée. Vérifiez et ajustez si besoin.
                    </p>
                  )}
                  {!bulkMode && compressionError && (
                    <p className="mt-3 text-sm text-orange-600">{compressionError}</p>
                  )}
                  {!bulkMode && analysisError && (
                    <p className="mt-3 text-sm text-red-600">{analysisError}</p>
                  )}
                </div>

                {/* Grid form */}
                {!bulkMode && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  {/* Titre / modèle */}
                  <div className="col-span-1 md:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Nom ou modèle
                    </label>
                    <input
                      type="text"
                      value={formData.title || ""}
                      onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                      placeholder="Ex : Air Max 90 blanche, Veste Nike Tech"
                      className="w-full h-11 rounded-xl border border-neutral-300 bg-white/80 px-4 focus:outline-none focus:ring-2 focus:ring-black/20"
                    />
                  </div>

                  {/* Type */}
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Type de vêtement *
                    </label>
                    <div className="relative">
                      <select
                        name="type"
                        required
                        className="w-full h-11 rounded-xl border border-neutral-300 bg-white/80 px-4 pr-10 focus:outline-none focus:ring-2 focus:ring-black/20"
                        value={formData.type}
                        onChange={(e) => setFormData((p) => ({ ...p, type: e.target.value }))}
                      >
                        <option value="" disabled>Choisir un type</option>
                        {TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Saison (segmented) */}
                  <div className="col-span-1">
                    <span className="block text-sm font-medium text-neutral-700 mb-2">Saison</span>
                    <div className="grid grid-cols-4 gap-2">
                      {SEASONS.map((s) => {
                        const active = formData.season === s;
                        return (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setSeason(s)}
                            className={`h-11 rounded-xl border text-sm capitalize transition
                              ${active ? "bg-black text-white border-black" : "bg-white/80 border-neutral-300 hover:bg-white"}`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Couleurs (swatches - multiple) */}
                  <div className="col-span-1 md:col-span-2">
                    <span className="block text-sm font-medium text-neutral-700 mb-2">
                      Couleurs {formData.colors.length > 0 && `(${formData.colors.length})`}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {COLORS.map((c) => {
                        const active = formData.colors.includes(c.value);
                        return (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => toggleColor(c.value)}
                            className={`relative w-10 h-10 rounded-full border transition
                              ${active ? "ring-2 ring-offset-2 ring-black" : "ring-0"}`}
                            style={{
                              background: c.swatch,
                              borderColor: c.ring || "rgba(0,0,0,0.12)",
                            }}
                            title={c.label}
                            aria-label={c.label}
                          >
                            {active && (
                              <span className="absolute inset-0 flex items-center justify-center text-white text-xl font-bold drop-shadow">
                                ✓
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Occasions (chips) */}
                  <div className="col-span-1 md:col-span-2">
                    <span className="block text-sm font-medium text-neutral-700 mb-2">Occasions</span>
                    <div className="flex flex-wrap gap-2">
                      {OCCASIONS.map((occ) => {
                        const active = formData.occasions.includes(occ);
                        return (
                          <button
                            key={occ}
                            type="button"
                            onClick={() => toggleOccasion(occ)}
                            className={`px-3 h-9 rounded-full text-sm capitalize border transition
                              ${active ? "bg-black text-white border-black" : "bg-white/80 border-neutral-300 hover:bg-white"}`}
                          >
                            {occ}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Détails */}
                  <div className="col-span-1 md:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Détails <span className="text-xs text-neutral-500">({detailsCount}/20)</span>
                    </label>
                    <input
                      type="text"
                      name="details"
                      maxLength={20}
                      value={formData.details}
                      onChange={(e) => setFormData((p) => ({ ...p, details: e.target.value }))}
                      placeholder="Ex: oversize, coupe droite…"
                      className="w-full h-11 rounded-xl border border-neutral-300 bg-white/80 px-4 focus:outline-none focus:ring-2 focus:ring-black/20"
                    />
                  </div>
                </div>
                )}
                </div>

                {/* Footer actions - Fixed at bottom */}
                <div className="px-6 md:px-8 py-4 border-t border-neutral-200/50 flex flex-col sm:flex-row gap-3 justify-end bg-white/80">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={bulkMode && bulkRunning}
                    className="h-11 px-5 rounded-xl border border-neutral-300 bg-white/70 hover:bg-white disabled:opacity-60"
                  >
                    {bulkMode ? (bulkRunning ? "Ajout en cours…" : "Fermer") : "Annuler"}
                  </button>
                  {!bulkMode && (
                    <button
                      type="submit"
                      disabled={submitting}
                      className="h-11 px-6 rounded-xl text-white bg-black hover:bg-neutral-900 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {submitting ? (isEditing ? "Enregistrement…" : "Ajout…") : (isEditing ? "Modifier" : "Ajouter +")}
                    </button>
                  )}
                </div>
              </form>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
